/**
 * Polygon.io API Service
 * Fetches real market data for portfolio comparison
 */

const POLYGON_API_KEY = import.meta.env.VITE_POLYGON_API_KEY;
const BASE_URL = 'https://api.polygon.io';

// Simple in-memory cache
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export interface PricePoint {
  date: string;
  value: number;
}

export interface PolygonAggregateBar {
  c: number;  // close price
  h: number;  // high price
  l: number;  // low price
  n: number;  // number of transactions
  o: number;  // open price
  t: number;  // timestamp (Unix ms)
  v: number;  // trading volume
  vw: number; // volume weighted average price
}

export interface PolygonAggregatesResponse {
  ticker: string;
  queryCount: number;
  resultsCount: number;
  adjusted: boolean;
  results: PolygonAggregateBar[];
  status: string;
  request_id: string;
  count: number;
}

/**
 * Get cached data or null if expired/missing
 */
function getCached<T>(key: string): T | null {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data as T;
  }
  return null;
}

/**
 * Set cache entry
 */
function setCache(key: string, data: any): void {
  cache.set(key, { data, timestamp: Date.now() });
}

/**
 * Format date as YYYY-MM-DD
 */
function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Fetch historical stock/ETF data from Polygon
 * @param ticker - Stock/ETF ticker symbol
 * @param years - Number of years of history
 * @param startingValue - Starting value to normalize prices to
 * @returns Price data and whether it covers the full requested range
 */
export async function fetchHistoricalData(
  ticker: string,
  years: number,
  startingValue: number = 100000
): Promise<PricePoint[]> {
  const cacheKey = `${ticker}-${years}-${startingValue}`;
  const cached = getCached<PricePoint[]>(cacheKey);
  if (cached) {
    return cached;
  }

  const endDate = new Date();
  const startDate = new Date();
  startDate.setFullYear(endDate.getFullYear() - years);

  const url = `${BASE_URL}/v2/aggs/ticker/${ticker}/range/1/day/${formatDate(startDate)}/${formatDate(endDate)}?adjusted=true&sort=asc&apiKey=${POLYGON_API_KEY}`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Polygon API error: ${response.status} ${response.statusText}`);
    }

    const data: PolygonAggregatesResponse = await response.json();

    if (!data.results || data.results.length === 0) {
      throw new Error(`No data returned for ${ticker}`);
    }

    // Check if we got enough data (at least 80% of expected trading days)
    const expectedTradingDays = years * 252;
    const actualDays = data.results.length;
    if (actualDays < expectedTradingDays * 0.8) {
      console.warn(`Polygon returned only ${actualDays} days for ${years} years (expected ~${expectedTradingDays}). API may have limited historical data.`);
      throw new Error(`Insufficient historical data: got ${actualDays} days, expected ${expectedTradingDays}`);
    }

    // Normalize prices to starting value
    const firstPrice = data.results[0].c;
    const normalizedData: PricePoint[] = data.results.map((bar) => ({
      date: new Date(bar.t).toISOString().split('T')[0],
      value: (bar.c / firstPrice) * startingValue,
    }));

    setCache(cacheKey, normalizedData);
    return normalizedData;
  } catch (error) {
    console.error(`Error fetching data for ${ticker}:`, error);
    throw error;
  }
}

/**
 * Fetch comparison data for multiple tickers
 * Returns data for user portfolio (simulated), benchmark (SPY), and selected fund
 */
export async function fetchComparisonData(
  selectedFundTicker: string,
  years: number,
  portfolioValue: number = 100000
): Promise<{
  userPortfolio: PricePoint[];
  benchmark: PricePoint[];
  tacticalFund: PricePoint[];
}> {
  const cacheKey = `comparison-${selectedFundTicker}-${years}-${portfolioValue}`;
  const cached = getCached<{
    userPortfolio: PricePoint[];
    benchmark: PricePoint[];
    tacticalFund: PricePoint[];
  }>(cacheKey);

  if (cached) {
    return cached;
  }

  try {
    // Fetch S&P 500 (SPY) and selected fund in parallel
    const [spyData, fundData] = await Promise.all([
      fetchHistoricalData('SPY', years, portfolioValue),
      selectedFundTicker === 'TACTICAL'
        ? simulateTacticalFund('SPY', years, portfolioValue)
        : fetchHistoricalData(selectedFundTicker, years, portfolioValue),
    ]);

    // For "user portfolio", we'll simulate a slightly different performance
    // In a real app, this would be calculated from actual holdings
    const userPortfolio = simulateUserPortfolio(spyData, portfolioValue);

    const result = {
      userPortfolio,
      benchmark: spyData,
      tacticalFund: fundData,
    };

    setCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Error fetching comparison data:', error);
    throw error;
  }
}

/**
 * Simulate a user portfolio based on SPY with some variance
 * In production, this would use actual portfolio holdings and weights
 */
function simulateUserPortfolio(benchmarkData: PricePoint[], startingValue: number): PricePoint[] {
  // Add some realistic variance to the benchmark
  // User portfolio typically has different risk/return characteristics
  const trackingError = 0.02; // 2% daily tracking error
  const underperformanceBias = 0.0001; // Slight underperformance vs benchmark (typical for diversified portfolio)

  let userValue = startingValue;
  const result: PricePoint[] = [];

  for (let index = 0; index < benchmarkData.length; index++) {
    const point = benchmarkData[index];

    if (index === 0) {
      result.push({ date: point.date, value: startingValue });
      continue;
    }

    // Seed random based on date for consistency
    const seed = new Date(point.date).getTime();
    const random = seededRandom(seed);

    // Calculate benchmark daily return
    const benchmarkReturn = (point.value - benchmarkData[index - 1].value) / benchmarkData[index - 1].value;

    // User portfolio: benchmark return + tracking error - slight underperformance
    const dailyVariance = (random - 0.5) * trackingError;
    const adjustedReturn = benchmarkReturn + dailyVariance - underperformanceBias;

    // Compound user portfolio value
    userValue = userValue * (1 + adjustedReturn);

    result.push({
      date: point.date,
      value: userValue,
    });
  }

  return result;
}

/**
 * Simulate a tactical fund that reduces exposure during downturns
 * This models a fund that shifts to cash/bonds during high volatility
 */
async function simulateTacticalFund(
  benchmarkTicker: string,
  years: number,
  startingValue: number
): Promise<PricePoint[]> {
  const benchmarkData = await fetchHistoricalData(benchmarkTicker, years, startingValue);

  // Calculate rolling volatility and drawdown to determine tactical allocation
  const lookbackWindow = 20; // 20-day rolling window
  let tacticalValue = startingValue;

  return benchmarkData.map((point, index) => {
    if (index === 0) {
      return { date: point.date, value: startingValue };
    }

    // Calculate recent drawdown
    const recentPrices = benchmarkData.slice(Math.max(0, index - lookbackWindow), index + 1);
    const peak = Math.max(...recentPrices.map(p => p.value));
    const drawdown = (peak - point.value) / peak;

    // Tactical allocation: reduce equity exposure during drawdowns
    // 100% equity when no drawdown, scaling down to 30% at 20%+ drawdown
    let equityAllocation = 1;
    if (drawdown > 0.05) {
      equityAllocation = Math.max(0.3, 1 - (drawdown * 3));
    }

    // Calculate return
    const benchmarkReturn = (point.value - benchmarkData[index - 1].value) / benchmarkData[index - 1].value;
    const cashReturn = 0.00015; // ~4% annual in cash

    const tacticalReturn = (benchmarkReturn * equityAllocation) + (cashReturn * (1 - equityAllocation));
    tacticalValue = tacticalValue * (1 + tacticalReturn);

    return {
      date: point.date,
      value: tacticalValue,
    };
  });
}

/**
 * Seeded random number generator for consistent results
 */
function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

/**
 * Check if Polygon API is available and configured
 */
export function isPolygonConfigured(): boolean {
  return !!POLYGON_API_KEY && POLYGON_API_KEY.length > 0;
}

/**
 * Clear the cache (useful for testing or manual refresh)
 */
export function clearCache(): void {
  cache.clear();
}
